import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { http } from "../lib/api";

const TIME_OPTIONS = [
  { label: "Quick read", minutes: 5 },
  { label: "A coffee break", minutes: 8 },
  { label: "A proper sit-down", minutes: 12 },
];

export default function EditorsPick({ slug }) {
  const [activeMinutes, setActiveMinutes] = useState(5);
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    http
      .get(`/posts/${slug}/recommendations`, {
        params: { max_minutes: activeMinutes, limit: 3 },
      })
      .then(({ data }) => setPicks(data.recommendations || []))
      .finally(() => setLoading(false));
    // `http` is a stable module import; setters are stable React refs.
  }, [slug, activeMinutes]);  

  let resultsBlock;
  if (loading) {
    resultsBlock = (
      <p className="font-mono text-[11px] tracking-widest uppercase text-[#66726d] animate-pulse">
        steeping…
      </p>
    );
  } else if (picks.length === 0) {
    resultsBlock = <p className="font-mono text-sm text-[#66726d]">No matching reads found.</p>;
  } else {
    resultsBlock = (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
        {picks.map((p) => (
          <Link
            key={p.slug}
            to={`/blog/${p.slug}`}
            className="group flex flex-col gap-4"
            data-testid={`editors-pick-card-${p.slug}`}
          >
            <div className="aspect-[4/3] overflow-hidden bg-[#f1eadb] card-img">
              <img
                src={p.cover_image}
                alt={p.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#1e4a35]">
                {p.category}
              </span>
              <span className="h-1 w-1 rounded-full bg-[#66726d]" />
              <span className="font-mono text-[10px] tracking-widest uppercase text-[#66726d] flex items-center gap-1.5">
                <Clock size={10} />
                {p.read_time} min
              </span>
            </div>
            <h3 className="font-serif text-2xl font-medium tracking-tight leading-tight text-[#151e1a] group-hover:text-[#1e4a35] transition-colors">
              {p.title}
            </h3>
            <p className="text-[#404e48] font-light leading-relaxed line-clamp-2">
              {p.excerpt}
            </p>
          </Link>
        ))}
      </div>
    );
  }

  return (
    <section
      className="border-y py-10 md:py-14 px-6 md:px-12"
      style={{ borderColor: "var(--border)" }}
      data-testid="editors-pick"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="overline mb-3">Editor's pick</p>
            <h2 className="font-serif text-3xl md:text-5xl font-light tracking-tight leading-[1.05]">
              Got <span className="italic text-[#1e4a35]">{activeMinutes}</span> more minutes?
              <br />
              Try one of these.
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="editors-pick-time-filter">
            {TIME_OPTIONS.map((opt) => {
              const active = opt.minutes === activeMinutes;
              return (
                <button
                  key={opt.minutes}
                  onClick={() => setActiveMinutes(opt.minutes)}
                  className={`px-4 py-2 font-mono text-[10px] tracking-widest uppercase transition-colors flex items-center gap-2 ${
                    active
                      ? "bg-[#1e4a35] text-white"
                      : "bg-[#f1eadb] text-[#404e48] hover:bg-[#e2d9c6]"
                  }`}
                  data-testid={`editors-pick-${opt.minutes}min`}
                >
                  <Clock size={12} />
                  <span>
                    {opt.minutes} min · {opt.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {resultsBlock}
      </div>
    </section>
  );
}
